import { useState, useEffect, useCallback } from "react";
import { api } from "../api.js";
import { card, labelStyle, inputStyle, selectStyle, btnSecondary, SectionTitle } from "./ui.js";

const STATUSES = [
  { key: "completed", label: "締結完了", bg: "#E1F5EE", text: "#0F6E56" },
  { key: "sent", label: "送信済み", bg: "#E6F1FB", text: "#185FA5" },
  { key: "signed", label: "顧客署名済", bg: "#EEEDFE", text: "#534AB7" },
  { key: "draft", label: "下書き", bg: "#F1EFE8", text: "#5F5E5A" },
  { key: "declined", label: "却下", bg: "#FCEBEB", text: "#A32D2D" },
  { key: "expired", label: "期限切れ", bg: "#FAEEDA", text: "#854F0B" },
  // サーバーが書く状態はすべて載せる。以前は not_found / canceled が無く「下書き」と表示され、
  // 追跡が止まった書類に気づけなかった（services/polling.ts・routes/cloudsign.ts の取り消し）
  { key: "not_found", label: "取得不可", bg: "#EDEBE5", text: "#3F3E3B" },
  { key: "canceled", label: "取り消し", bg: "#FCEBEB", text: "#A32D2D" },
];

function Pill({ status }: { status: string }) {
  // 未知の状態を「下書き」に見せない。そのままの値を出す
  const s = STATUSES.find((x) => x.key === status) || { label: status, bg: "#F1EFE8", text: "#5F5E5A" };
  return <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 12, fontSize: 13, fontWeight: 500, background: s.bg, color: s.text }}>{s.label}</span>;
}

const OBJ_LABELS: Record<string, string> = { deals: "取引", companies: "会社", contacts: "コンタクト" };

export default function LogsTab() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [objectFilter, setObjectFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(limit) };
      if (statusFilter !== "all") params.status = statusFilter;
      if (objectFilter !== "all") params.objectType = objectFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const data = await api.getLogs(params);
      setLogs(data.logs || []);
      setTotal(data.pagination?.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, objectFilter, searchTerm, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // 検索やフィルタ変更時にページを1に戻す
  useEffect(() => { setPage(1); }, [statusFilter, objectFilter, searchTerm, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <SectionTitle>送信ログ</SectionTitle>

      <div style={{ ...card, display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={labelStyle}>レコードID検索</div>
          <input style={inputStyle} type="text" placeholder="HubSpot レコードIDで検索..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div style={{ width: 160 }}>
          <div style={labelStyle}>送信日（開始）</div>
          <input style={inputStyle} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div style={{ width: 160 }}>
          <div style={labelStyle}>送信日（終了）</div>
          <input style={inputStyle} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div style={{ width: 180 }}>
          <div style={labelStyle}>ステータス</div>
          <select style={selectStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">すべて</option>
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ width: 180 }}>
          <div style={labelStyle}>オブジェクト</div>
          <select style={selectStyle} value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
            <option value="all">すべて</option>
            <option value="deals">取引</option>
            <option value="companies">会社</option>
            <option value="contacts">コンタクト</option>
          </select>
        </div>
        <button style={{ ...btnSecondary, height: 42 }} onClick={() => {
          const params = new URLSearchParams();
          if (statusFilter !== "all") params.set("status", statusFilter);
          if (objectFilter !== "all") params.set("objectType", objectFilter);
          if (dateFrom) params.set("dateFrom", dateFrom);
          if (dateTo) params.set("dateTo", dateTo);
          // 新規タブでも同一オリジンの httpOnly Cookie は自動送信されるので token 不要
          window.open(`/api/send-logs/export/csv?${params.toString()}`, "_blank");
        }}>CSV出力</button>
      </div>

      <div style={{ fontSize: 14, color: "#6b6b68", marginBottom: 14 }}>
        {loading ? "読み込み中..." : `${total} 件の結果`}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", fontSize: 15, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#faf9f7" }}>
              {["レコードID", "オブジェクト", "ステータス", "宛先数", "送信日時"].map((h) => (
                <th key={h} style={{ textAlign: "left", fontWeight: 500, color: "#6b6b68", padding: "12px 14px", borderBottom: "1px solid #e8e7e3", fontSize: 13 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => {
              const sentDate = log.sentAt ? new Date(log.sentAt).toLocaleDateString("ja-JP") : "—";
              const sentTime = log.sentAt ? new Date(log.sentAt).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <>
                  <tr key={log.id} onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                    style={{ cursor: "pointer", background: expandedRow === log.id ? "#faf9f7" : "white" }}>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0efeb", fontFamily: "monospace", fontSize: 13, color: "#6b6b68" }}>
                      {log.hubspotRecordId}
                    </td>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0efeb" }}>
                      <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 8, background: "#f5f4f0", color: "#6b6b68" }}>
                        {OBJ_LABELS[log.hubspotObjectType] || log.hubspotObjectType}
                      </span>
                    </td>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0efeb" }}><Pill status={log.status} /></td>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0efeb", color: "#6b6b68", textAlign: "center" }}>{log.recipientCount || 0}</td>
                    <td style={{ padding: "12px 14px", borderBottom: "1px solid #f0efeb", color: "#6b6b68" }}>{sentDate} {sentTime}</td>
                  </tr>
                  {expandedRow === log.id && (
                    <tr key={`${log.id}-detail`}>
                      <td colSpan={5} style={{ padding: "0 14px 14px", background: "#faf9f7", borderBottom: "1px solid #e8e7e3" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, padding: "14px 0", fontSize: 14 }}>
                          <div>
                            <div style={{ color: "#6b6b68", marginBottom: 4 }}>HubSpot レコードID</div>
                            <div style={{ fontFamily: "monospace", fontWeight: 500 }}>{log.hubspotRecordId}</div>
                          </div>
                          <div>
                            <div style={{ color: "#6b6b68", marginBottom: 4 }}>クラウドサイン書類ID</div>
                            <div style={{ fontFamily: "monospace", fontWeight: 500 }}>{log.csDocumentId || "—"}</div>
                          </div>
                          <div>
                            <div style={{ color: "#6b6b68", marginBottom: 4 }}>ファイル数</div>
                            <div style={{ fontWeight: 500 }}>{log.fileCount}</div>
                          </div>
                          <div>
                            <div style={{ color: "#6b6b68", marginBottom: 4 }}>下書きURL</div>
                            <div style={{ color: log.csDraftUrl ? "#185FA5" : "#6b6b68", fontWeight: 500, wordBreak: "break-all" }}>
                              {log.csDraftUrl || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: "#6b6b68", marginBottom: 4 }}>締結完了URL</div>
                            <div style={{ color: log.csCompletedUrl ? "#185FA5" : "#6b6b68", fontWeight: 500 }}>
                              {log.csCompletedUrl || "—"}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: "#6b6b68", marginBottom: 4 }}>最終更新</div>
                            <div style={{ fontWeight: 500 }}>{log.updatedAt ? new Date(log.updatedAt).toLocaleString("ja-JP") : "—"}</div>
                          </div>
                        </div>
                        {/* PDF ダウンロード（締結完了のみ）。Cookie 認証なので token 不要 */}
                        {log.status === "completed" && log.csDocumentId && (
                          <div style={{ display: "flex", gap: 10, paddingTop: 14, borderTop: "1px solid #e0dfd9" }}>
                            <button onClick={() => {
                              window.open(`/api/cloudsign/documents/${log.csDocumentId}/signed-pdf`, "_blank");
                            }} style={{ ...btnSecondary, fontSize: 13, padding: "6px 14px" }}>署名済みPDFをダウンロード</button>
                            <button onClick={() => {
                              window.open(`/api/cloudsign/documents/${log.csDocumentId}/agreement-pdf`, "_blank");
                            }} style={{ ...btnSecondary, fontSize: 13, padding: "6px 14px" }}>合意締結証明書をダウンロード</button>
                          </div>
                        )}
                        {/* 送信取り消し（sent 状態のみ） */}
                        {log.status === "sent" && log.csDocumentId && (
                          <div style={{ display: "flex", gap: 10, paddingTop: 14, borderTop: "1px solid #e0dfd9" }}>
                            <button onClick={async () => {
                              if (!confirm("この書類を取り消しますか？受信者は署名できなくなります。")) return;
                              try {
                                await (await import("../api.js")).api.cancelDocument(log.csDocumentId);
                                alert("取り消しました");
                                location.reload();
                              } catch (e: any) {
                                alert("取り消しに失敗しました: " + e.message);
                              }
                            }} style={{ ...btnSecondary, fontSize: 13, padding: "6px 14px", color: "#A32D2D", borderColor: "#f0c8c8" }}>
                              書類を取り消し
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={5} style={{ padding: "48px 0", textAlign: "center", color: "#6b6b68", fontSize: 15 }}>
                {total === 0 ? "送信ログはまだありません" : "条件に一致するログがありません"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, fontSize: 14, color: "#6b6b68" }}>
        <span>{total > 0 ? `${(page - 1) * limit + 1} - ${Math.min(page * limit, total)} / ${total} 件` : "0 件"}</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13, opacity: page <= 1 ? 0.4 : 1 }}>前へ</button>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
            style={{ ...btnSecondary, padding: "6px 14px", fontSize: 13, opacity: page >= totalPages ? 0.4 : 1 }}>次へ</button>
        </div>
      </div>
    </>
  );
}
